import { useMemo } from 'react';
import {
  ResourceRowData,
  ResourceRowDataWithRoles,
} from '@features/components/CaseOverview/components/AssignedResources/types';
import { ResourceTable } from '@features/components/CaseOverview/components/AssignedResources/ResourceTable';
import { BASIC_COLUMNS } from '@features/components/CaseOverview/components/AssignedResources/config';
import { AppType, useAppContext } from '@features/contexts/AppProvider';
import { CustomerMT, CustomerOrderByOptions, CustomerRoleT } from '@/api/apis/customer';
import { useInfiniteCustomerListQuery } from '@/queries/customer';
import { Uuid } from '@/api/types';
import { useInfiniteList } from '@/hooks/useInfiniteList';

export type TxCenterContactsListProps = {
  searchTerm: string;
  usersToAssign: Partial<Record<CustomerRoleT, string[]>>;
  assignedUsersByRoles?: string[];
  onUserAssign: (user: ResourceRowData) => void;
  selectedRole: CustomerRoleT;
  txCenterId?: Uuid;
};

export const mapCustomerRowData = (
  customer: CustomerMT,
  usersToAssign: Partial<Record<CustomerRoleT, string[]>>,
  selectedRole: CustomerRoleT,
  txCenterId?: string,
): ResourceRowDataWithRoles => {
  const { id, userId, firstName, lastName, photoPath, primaryPhone, secondaryPhone, customerInTxCenters } = customer;
  const roles = customerInTxCenters?.find((i) => i.txCenterId === txCenterId)?.roles || [];
  return {
    id,
    userId,
    firstName,
    lastName,
    photoPath,
    primaryPhone,
    secondaryPhone,
    roles,
    assigned: usersToAssign[selectedRole]?.includes(id) || false,
  };
};

export const TxCenterContactsTable = ({
  searchTerm,
  assignedUsersByRoles,
  usersToAssign,
  onUserAssign,
  selectedRole,
  txCenterId,
}: TxCenterContactsListProps) => {
  const { type } = useAppContext();
  const priorityCustomerIds = useMemo(() => {
    return type === AppType.NopAccessPlatform ? assignedUsersByRoles?.map((id) => id).join(',') : undefined;
  }, [assignedUsersByRoles, type]);

  const { list, hasNextPage, fetchNextPage, isLoading } = useInfiniteList({
    query: useInfiniteCustomerListQuery,
    queryParams: {
      roles: selectedRole,
      txCenters: txCenterId,
      priorityCustomerIds,
      enabled: true,
      orderBy: CustomerOrderByOptions.ContactName,
      asc: true,
      itemsPerPage: 20,
    },
    searchTerm,
  });

  const tableRows = useMemo(
    () => list.map((customer) => mapCustomerRowData(customer, usersToAssign, selectedRole, txCenterId)),
    [usersToAssign, txCenterId, selectedRole, list],
  );

  const assignedCount = tableRows.filter((r) => r.assigned).length;

  const tableRowsWithLastDetach = tableRows.map((row) => ({
    ...row,
    isLastAssigned: row.assigned && assignedCount === 1,
  }));

  return (
    <ResourceTable
      columns={BASIC_COLUMNS}
      onUserAssign={onUserAssign}
      list={tableRowsWithLastDetach}
      hasNextPage={hasNextPage}
      isFirstLoading={isLoading}
      loadNextPage={fetchNextPage}
      selectedRole={selectedRole}
    />
  );
};
